import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Trophy, Target, MessageCircle, Swords, Star, TrendingUp, Zap, Shield, ChevronRight } from 'lucide-react';
import { MobileBottomNav } from '@/components/MobileNav';

const API_URL = process.env.REACT_APP_BACKEND_URL?.replace(/^http:\/\//i, 'https://') + '/api';

const TIER_COLORS = {
  bronze: 'from-orange-700/20 to-orange-900/10 border-orange-600/30',
  silver: 'from-gray-400/20 to-gray-600/10 border-gray-400/30',
  gold: 'from-yellow-500/20 to-yellow-700/10 border-yellow-500/30',
  platinum: 'from-cyan-400/20 to-cyan-600/10 border-cyan-400/30',
  diamond: 'from-purple-400/20 to-purple-600/10 border-purple-400/30',
  champion: 'from-[#00BFA6]/20 to-[#00BFA6]/10 border-[#00BFA6]/30',
};

const TIER_TEXT = {
  bronze: 'text-orange-400', silver: 'text-gray-300', gold: 'text-yellow-400',
  platinum: 'text-cyan-400', diamond: 'text-purple-400', champion: 'text-[#00BFA6]',
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/profile/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setProfile(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [token]);

  if (loading) return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#00BFA6] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const p = profile?.profile || {};
  const stats = profile?.stats || {};
  const level = p.level || {};
  const tier = level.tier || 'bronze';
  const sportAccuracy = profile?.sport_accuracy || [];

  return (
    <div className="min-h-screen bg-[#121212] pb-24">
      <header className="bg-[#1E1E1E] border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <User className="w-6 h-6 text-[#7B1FA2]" />
          <h1 className="text-xl font-bold text-white">Mein Profil</h1>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Level & XP Hero */}
        <div data-testid="profile-level-card" className={`p-5 rounded-2xl border bg-gradient-to-br ${TIER_COLORS[tier]}`}>
          <div className="flex items-center gap-4 mb-3">
            <div className={`w-14 h-14 rounded-2xl bg-black/30 flex items-center justify-center border ${TIER_COLORS[tier].split(' ').pop()}`}>
              <span className={`text-2xl font-black font-mono ${TIER_TEXT[tier]}`}>{level.level || 1}</span>
            </div>
            <div>
              <div className="text-lg font-bold text-white">{p.username || 'Anonym'}</div>
              <div className={`text-sm font-medium ${TIER_TEXT[tier]}`}>{level.title || 'Anfaenger'}</div>
            </div>
          </div>
          <div className="mb-1 flex justify-between text-[10px] text-gray-400">
            <span>{level.current_xp || 0} XP</span>
            <span>{level.next_level_xp || 100} XP</span>
          </div>
          <div className="w-full h-2 bg-black/20 rounded-full">
            <div className={`h-2 rounded-full transition-all bg-gradient-to-r ${
              tier === 'champion' ? 'from-[#00BFA6] to-[#00BFA6]' :
              tier === 'diamond' ? 'from-purple-400 to-purple-600' :
              tier === 'gold' ? 'from-yellow-400 to-yellow-600' :
              'from-gray-400 to-gray-500'
            }`} style={{ width: `${level.progress || 0}%` }} />
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-2">
          {[
            { label: 'Tipps', value: stats.tips_count || 0, icon: Target },
            { label: 'Quote', value: `${stats.accuracy || 0}%`, icon: TrendingUp },
            { label: 'Challenges', value: stats.challenges_played || 0, icon: Swords },
            { label: 'Chat', value: stats.chat_messages || 0, icon: MessageCircle },
          ].map((s, i) => (
            <div key={i} className="p-3 bg-[#1E1E1E] rounded-xl border border-white/5 text-center">
              <s.icon className="w-4 h-4 text-gray-500 mx-auto mb-1" />
              <div className="text-sm font-bold font-mono text-white">{s.value}</div>
              <div className="text-[9px] text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Social Stats */}
        <div className="flex gap-2">
          <div className="flex-1 p-3 bg-[#1E1E1E] rounded-xl border border-white/5 text-center">
            <div className="text-lg font-bold text-white">{p.followers_count || 0}</div>
            <div className="text-[10px] text-gray-500">Follower</div>
          </div>
          <div className="flex-1 p-3 bg-[#1E1E1E] rounded-xl border border-white/5 text-center">
            <div className="text-lg font-bold text-white">{p.following_count || 0}</div>
            <div className="text-[10px] text-gray-500">Folge ich</div>
          </div>
          <div className="flex-1 p-3 bg-[#1E1E1E] rounded-xl border border-white/5 text-center">
            <div className="text-lg font-bold text-[#7B1FA2]">{profile?.achievements_unlocked || 0}</div>
            <div className="text-[10px] text-gray-500">Achievements</div>
          </div>
        </div>

        {/* Sport Accuracy */}
        {sportAccuracy.length > 0 && (
          <div>
            <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2">
              <Target className="w-4 h-4 text-[#00BFA6]" />
              Genauigkeit pro Sportart
            </h3>
            <div className="space-y-1.5">
              {sportAccuracy.map((s, i) => (
                <div key={i} className="flex items-center gap-3 p-2.5 bg-[#1E1E1E] rounded-lg border border-white/5">
                  <span className="text-xs text-white w-24">{s.sport}</span>
                  <div className="flex-1 h-1.5 bg-white/5 rounded-full">
                    <div className="h-1.5 rounded-full bg-[#00BFA6]" style={{ width: `${Math.min(s.accuracy, 100)}%` }} />
                  </div>
                  <span className="text-xs font-mono text-[#00BFA6] w-12 text-right">{s.accuracy}%</span>
                  <span className="text-[9px] text-gray-500 w-16 text-right">{s.total} Tipps</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="space-y-1.5">
          {[
            { path: '/social', label: 'Community & Rangliste', icon: Trophy },
            { path: '/ai-challenges', label: 'User vs KI Challenges', icon: Swords },
            { path: '/multi-sport', label: 'Multi-Sport Predictions', icon: TrendingUp },
            { path: '/settings', label: 'Einstellungen', icon: Shield },
          ].map((link, i) => (
            <button
              key={i}
              onClick={() => navigate(link.path)}
              className="w-full flex items-center gap-3 p-3 bg-[#1E1E1E] rounded-xl border border-white/5 hover:border-[#00BFA6]/20 transition-all text-left"
            >
              <link.icon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-white flex-1">{link.label}</span>
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          ))}
        </div>
      </div>
      <MobileBottomNav />
    </div>
  );
};

export default ProfilePage;
